'use client';

import {
  Box,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  PathType,
  type SecurityScanResultEvidenceTypeEnum,
  SecurityScanResultTypeEnum,
  SecurityScanDetectorTypeEnum,
  type InventoryResource,
  type SecurityScan,
  type SecurityScanResult,
  type SecurityScansSummaryResponse,
} from '@repo/api-gw-sdk';
import { Fragment, useState } from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';
import { useViewParameters } from '@/contexts/navigation';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BaseAction } from '@/data/inventory/actions';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { isDateValid } from '@/utils/dateTime';
import { dayjs, DATE_FORMAT } from '@/utils/dayjs';

import { ExcludeFromRansomwareDialog } from './excludeFromRansomwareDialog';
import { getEvidenceStrings } from './securityUtils';
import { SkullIcon } from './skullIcon';

import { Accordion, AccordionDetails, AccordionSummary } from '../accordion';
import { PathLink } from '../explorer/fileLink';
import { StatusCardHeader } from '../panels/instance/tabs/overview-tab/statusCardHeader';
import { StatusCardWrapper } from '../panels/instance/tabs/overview-tab/statusCardWrapper';
import { Panels } from '../panels/panels';
import { SnapshotIndicator } from '../snapshots/SnapshotIndicator';
import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';

const DETECTOR_CONFIG: Record<string, { title: string; borderColor: string }> =
  {
    [SecurityScanDetectorTypeEnum.RansomwareBehavior]: {
      title: 'Ransomware Detection',
      borderColor: 'var(--mui-palette-error-main)',
    },
    [SecurityScanDetectorTypeEnum.Malware]: {
      title: 'Malware Detected',
      borderColor: 'var(--mui-palette-error-main)',
    },
  };

interface SecurityScanCardProps {
  resource: InventoryResource;
  onExclusionChanged: () => Promise<void>;
  detectorType: SecurityScanDetectorTypeEnum;
  scans?: SecurityScansSummaryResponse;
  isLoading?: boolean;
}

export const SecurityScanCard = (props: SecurityScanCardProps) => {
  const { resource, onExclusionChanged, detectorType, scans, isLoading } =
    props;
  const dal = useDAL();
  const { isAuthorizedResource } = useRoles();
  const { rightPanel } = useWorkspace();
  const { removeParam } = useViewParameters();
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);

  const isRansomware =
    detectorType === SecurityScanDetectorTypeEnum.RansomwareBehavior;
  const snapshotId = isRansomware ? scans?.lastClean?.snapshotId : undefined;
  const snapshot =
    dal.inventory.snapshots.useSnapshot(snapshotId).data?.snapshot;

  const results: SecurityScanResult[] = scans?.lastScan?.results || [];
  const resultScores = results
    .map((r) => r.score)
    .filter((s): s is number => s !== null);
  const confidenceScore =
    scans?.lastScan?.confidenceScore ??
    (resultScores.length > 0
      ? resultScores.reduce((a, b) => a + b, 0) / resultScores.length
      : null);
  const config =
    DETECTOR_CONFIG[detectorType] ??
    DETECTOR_CONFIG[SecurityScanDetectorTypeEnum.RansomwareBehavior];
  const groupedEvidences = results.reduce((map, r) => {
    const key = r.evidenceType;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
    return map;
  }, new Map<SecurityScanResultEvidenceTypeEnum, SecurityScanResult[]>());
  const actions: BaseAction[] = isRansomware
    ? [
        {
          title: 'Exclude from ransomware detection',
          execute: () => {
            setExcludeDialogOpen(true);
          },
          disabled: () =>
            !isAuthorizedResource('update:exclude_resource', resource),
          id: 'exclude-security-alert',
          tooltip: '',
          icon: '',
        },
      ]
    : [];

  const description = isRansomware
    ? `${scans?.infectedSnapshotsCount || 0} ${scans?.infectedSnapshotsCount === 1 ? 'snapshot has' : 'snapshots have'} ransomware infection indicators.`
    : `Malware indicators detected in snapshots.`;

  async function excludeAlerts(): Promise<void> {
    await dal.security.excludeResource(resource.id);
    await onExclusionChanged();
    setExcludeDialogOpen(false);
  }

  const onRestore = () => {
    if (!snapshot) {
      return;
    }
    const vols = snapshot.resource?.properties?.awsEc2?.volumes || [];

    const onClose = () => {
      rightPanel.setIsOpen(false);
      removeParam('id');
    };

    const onBack = () => {
      rightPanel.setComponent({
        panel: Panels.InventoryInstance,
        props: {
          id: resource.id,
          onClose,
          initialTab: 'overview',
        },
      });
    };

    rightPanel.setComponent({
      panel: Panels.RestoreEc2InstanceWizard,
      props: {
        snapshot: snapshot,
        resourceName: resource.resourceName,
        volumes: vols,
        onClose,
        onBack,
      },
    });
  };

  return (
    <StatusCardWrapper borderColor={config.borderColor}>
      <Stack gap='24px'>
        <Stack marginBottom='12px' alignItems={'center'} spacing={'12px'}>
          <Stack
            direction='row'
            width='100%'
            padding='0 24px 0 40px'
            alignItems='center'
          >
            <StatusCardHeader
              title={config.title}
              actions={[]}
              entity={resource}
            />
            <Box sx={{ flex: 1 }} />
            {confidenceScore !== null && (
              <Tooltip title='Score' placement='top'>
                <Chip
                  label={`${Math.round(confidenceScore * 100)}%`}
                  size='small'
                  sx={{
                    backgroundColor: 'var(--mui-palette-error-main)',
                    color: 'var(--mui-palette-error-contrastText)',
                    fontWeight: 600,
                    fontSize: '11px',
                    cursor: 'default',
                  }}
                />
              </Tooltip>
            )}
            {!!actions.length && (
              <OptionMenu
                options={actions.map((action) => ({
                  text: action.title || action.tooltip,
                  menuItemProps: {
                    onClick: () => action.execute(resource),
                    disabled: action.disabled?.(),
                  },
                  disabledTooltip: action.disabledTooltip,
                  id: action.id,
                }))}
              />
            )}
          </Stack>
          <Typography variant='h3'>
            {isRansomware ? 'Ransomware Behavior Detected' : 'Malware detected'}
          </Typography>
        </Stack>
        <Stack direction='row' marginY='12px' marginX='40px' gap='40px'>
          {isLoading ? (
            <Stack gap='4px'>
              <Skeleton width='250px' animation='wave' />
              <Skeleton width='200px' animation='wave' />
              <Skeleton width='230px' animation='wave' />
            </Stack>
          ) : (
            <>
              <Stack gap='4px' flex={60} paddingTop='6px'>
                <Typography variant='subtitle1' lineHeight='20px'>
                  {description}
                </Typography>
                {scans?.lastScan?.justification && (
                  <Typography
                    variant='subtitle1'
                    color='text.secondary'
                    lineHeight='20px'
                  >
                    {scans.lastScan.justification}
                  </Typography>
                )}
              </Stack>
              {isRansomware && (
                <>
                  <Divider orientation='vertical' flexItem />
                  <Stack flex={40} gap='12px' justifyContent='space-around'>
                    {scans?.lastClean &&
                    isDateValid(scans.lastClean.createdTime) ? (
                      <SnapshotSummary
                        resource={resource}
                        scan={scans.lastClean}
                        title='Latest clean'
                        onRestore={onRestore}
                        showRestoreButton
                      />
                    ) : (
                      <EmptySnapshotSummary title='Latest clean' />
                    )}
                    {scans?.firstInfected &&
                    isDateValid(scans.firstInfected.createdTime) ? (
                      <SnapshotSummary
                        resource={resource}
                        scan={scans.firstInfected}
                        title='First infected'
                      />
                    ) : (
                      <EmptySnapshotSummary title='First infected' />
                    )}
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>
        <Divider sx={{ margin: '0 40px', borderStyle: 'dashed' }} />
        <Typography paddingX='40px' variant='subtitle1'>
          {`Suspicious indicators from the latest snapshot on ${dayjs.utc(scans?.lastScan?.createdTime).format(DATE_FORMAT)}:`}
        </Typography>
        <Stack marginY='12px' gap='8px'>
          {isLoading ? (
            <Skeleton
              sx={{ marginLeft: '40px', marginRight: '24px' }}
              animation='wave'
            />
          ) : (
            [...groupedEvidences.entries()].map(
              ([evidenceType, groupResults], idx) => {
                return (
                  <Fragment key={evidenceType}>
                    {!!idx && <Divider />}
                    <EvidenceDetails
                      evidenceType={evidenceType}
                      results={groupResults}
                      resource={resource}
                    />
                  </Fragment>
                );
              }
            )
          )}
        </Stack>
      </Stack>
      {isRansomware && (
        <ExcludeFromRansomwareDialog
          isOpen={excludeDialogOpen}
          onCancel={() => setExcludeDialogOpen(false)}
          triggerExclusion={excludeAlerts}
          resourceName={resource.resourceName}
        />
      )}
    </StatusCardWrapper>
  );
};

interface EvidenceDetailsProps {
  evidenceType: SecurityScanResultEvidenceTypeEnum;
  results: SecurityScanResult[];
  resource: InventoryResource;
}

const EvidenceDetails = ({
  evidenceType,
  results,
  resource,
}: EvidenceDetailsProps) => {
  const evidenceStrings = getEvidenceStrings(evidenceType);
  const [isOpen, setIsOpen] = useState(false);
  const count = results.length;

  return (
    <Accordion
      expanded={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      disableGutters
      sx={{ marginX: '40px' }}
    >
      <AccordionSummary>
        <Stack width='100%' direction='row' justifyContent='space-between'>
          <Typography variant='h4'>{evidenceStrings.label}</Typography>
          {!isOpen && <Typography variant='h4'>{count}</Typography>}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography marginBottom='16px'>{`${count} ${evidenceStrings.entity}${count === 1 ? ' was' : 's were'} ${evidenceStrings.description}.${results.some((r) => r.path) ? ' Here are some samples:' : ''}`}</Typography>
        <Stack gap='16px'>
          {results
            .filter((r): r is typeof r & { path: string } => !!r.path)
            .map((result) => (
              <Stack
                direction={'row'}
                gap='12px'
                key={result.path}
                alignItems='center'
              >
                <SkullIcon />
                <PathLink
                  className='text-[var(--mui-palette-error-main)]! [&:hover]:text-[var(--mui-palette-error-light)]!'
                  entity={resource}
                  path={result.path}
                  pathType={
                    result.type === SecurityScanResultTypeEnum.Directory
                      ? PathType.Directory
                      : PathType.File
                  }
                />
              </Stack>
            ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const SnapshotSummary = (props: {
  resource: InventoryResource;
  scan: SecurityScan;
  title: string;
  onRestore?: () => void;
  showRestoreButton?: boolean;
}) => {
  const { scan, title, showRestoreButton, onRestore, resource } = props;

  const dal = useDAL();
  const { isAuthorizedResource } = useRoles();
  const { vaults } = useBackupVaults();
  const { data: snapshot, isLoading } = dal.inventory.snapshots.useSnapshot(
    scan.snapshotId
  );
  const vault = vaults.find((v) => v.id === snapshot?.snapshot.vaultId);

  if (isLoading) {
    return <Skeleton />;
  }

  const isAuthToRestore = isAuthorizedResource(
    'create:restore_resource',
    resource
  );

  return (
    <Stack
      gap='12px'
      direction='row'
      alignItems='center'
      justifyContent='space-between'
    >
      <Typography width='120px'>{title}</Typography>
      <Stack direction='row' gap='8px' alignItems='center'>
        <SnapshotIndicator
          sx={{ margin: '4px' }}
          backgroundColor={vault?.backgroundColor || null}
        />
        <Typography noWrap>
          {dayjs.utc(scan.createdTime).format(DATE_FORMAT)}
        </Typography>
        {showRestoreButton ? (
          <AuthorizationTooltip title='Restore' isAuthorized={isAuthToRestore}>
            <span>
              <IconButton
                disabled={!isAuthToRestore}
                color='primary'
                size='small'
                onClick={onRestore}
              >
                <i className='material-symbols-settings-backup-restore-rounded' />
              </IconButton>
            </span>
          </AuthorizationTooltip>
        ) : (
          <Box width='32px' />
        )}
      </Stack>
    </Stack>
  );
};

const EmptySnapshotSummary = (props: { title: string }) => {
  const { title } = props;
  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Typography flexGrow={1}>{title}</Typography>
      <Typography marginRight='40px'>None</Typography>
    </Stack>
  );
};
